import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { useAuth } from '../providers'
import { v1Client } from '../api'
import type { paths } from '../types/backend-openapi'
import { serverErrorMessage } from './useCheckout'
import { refreshBilling } from '../store/global-billing'

export type ClaimMigrationOfferResult =
  paths['/billing/migration-offer/claim']['post']['responses'][200]['content']['application/json']['data']

export interface ClaimMigrationOfferVariables {
  /** The tier the user picked their welcome-back trial on; defaults server-side to their 1.0 tier. */
  tier?: 'lite' | 'pro2'
  /** The monthly price the screen quoted. The server refuses anything but that tier's list price. */
  acceptedMonthlyPriceUsd?: number
  /**
   * The trial length the screen quoted for that tier. The server converts at a quoted month even
   * where its own rule now says less, and refuses any other mismatch.
   */
  acceptedTrialDays?: number
}

/**
 * Claims the 1.0 → 2.0 welcome-back trial, which the server applies in place with no card.
 * A gateway from before card-less trials still answers `checkout` for a card-less user, and
 * this opens it in the browser for its webhook to finish. Regenerating the API types against
 * a current gateway removes that variant and this branch with it.
 */
export function useClaimMigrationOffer(): UseMutationResult<
  ClaimMigrationOfferResult,
  Error,
  ClaimMigrationOfferVariables
> {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationFn: async (variables: ClaimMigrationOfferVariables) => {
      const token = await getAuthToken()
      const response = await v1Client.POST('/billing/migration-offer/claim', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: variables,
      })

      if (response.error) {
        throw new Error(serverErrorMessage(response.error) ?? 'Failed to claim')
      }

      return response.data.data
    },
    onSuccess: (result) => {
      if (result.outcome === 'checkout') {
        window.ampltd.trackEvent('Subscription', {
          action: 'checkout initiated',
          source: 'hl1-migration',
        })
        window.open(result.checkoutUrl, '_blank')
      }
      refreshBilling()
    },
    onError: (error) => {
      console.error('Migration offer claim error:', error)
      window.ampltd.trackEvent('Subscription', {
        action: 'migration offer claim error',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })
}
